import { ClientError, errorMessages } from '@lib/client-error';

export function extensionFromName(name: string) {
  const splitName = name.split('.');

  return splitName[splitName.length - 1];
}

export type FileType = 'excel' | 'powerpoint' | 'illustrator' | 'word' | 'pdf' | 'unknownFiletype';

export function fileTypeFromExtension(extension: string): FileType {
  const excelExtensions = ['xls', 'xlsx', 'xlsm', 'xlsb', 'xltx'];
  const illustratorExtensions = ['ai', 'ait'];
  const wordExtensions = ['doc', 'docm', 'dot', 'docx'];
  const powerpointExtensions = ['pptx', 'pptm', 'ppt'];

  if (excelExtensions.includes(extension)) {
    return 'excel';
  }

  if (powerpointExtensions.includes(extension)) {
    return 'powerpoint';
  }

  if (illustratorExtensions.includes(extension)) {
    return 'illustrator';
  }

  if (wordExtensions.includes(extension)) {
    return 'word';
  }

  if (extension === 'pdf') {
    return 'pdf';
  }

  return 'unknownFiletype';
}

/** A PDF, or an Illustrator file, which is one when saved PDF compatible as Illustrator does by default. */
export function isPdfDocument(extension: string) {
  return extension === 'pdf' || fileTypeFromExtension(extension) === 'illustrator';
}

export function fileTypeFromFileName(name: string) {
  return fileTypeFromExtension(extensionFromName(name));
}

// Copied from https://stackoverflow.com/questions/15900485/correct-way-to-convert-size-in-bytes-to-kb-mb-gb-in-javascript
export function formatBytes(bytes: number, decimals = 1) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function isImage(fileExtension: string) {
  const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'tiff', 'bmp', 'svg', 'heic', 'avif', 'ico'];

  return imageExtensions.includes(fileExtension);
}

export type BlobStorageImage = {
  path: string;
  name: string;
  size: number;
  height: number;
  width: number;
};

// An image upload as what its bytes are, never as what its name claims: anything that isn't one of the
// accepted image formats is refused, and the type is relabelled to match the content. HEIC is converted
// to JPEG, which every browser can draw.
export async function ensureFileFormat(file: File): Promise<File> {
  if (file.size === 0) throw new ClientError(errorMessages.upload_file_empty);
  const type = await sniffImageType(file);
  if (!type) throw new ClientError(errorMessages.upload_image_invalid_type);

  if (type !== 'image/heic') {
    return file.type === type ? file : new File([file], file.name, { type, lastModified: file.lastModified });
  }

  const { heicTo } = await import('heic-to');
  const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 }).catch(() => {
    throw new ClientError(errorMessages.upload_image_heic_unreadable);
  });
  return new File([jpeg], file.name.replace(/\.(heic|heif)$/i, '.jpg'), { type: 'image/jpeg' });
}

// Through whichever endpoint the caller stores files with.
export async function uploadImageWith(file: File, upload: (file: File) => Promise<string>): Promise<BlobStorageImage> {
  const image = await ensureFileFormat(file);
  const [path, dimensions] = await Promise.all([upload(image), getImageDimensions(image)]);
  return { path, name: image.name, size: image.size, ...dimensions };
}

export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      window.URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      window.URL.revokeObjectURL(img.src);
      reject(new ClientError(errorMessages.upload_image_invalid_type));
    };
    img.src = window.URL.createObjectURL(file);
  });
}

/**
 * The MIME type of the image the bytes are, whatever the name or file.type say: the browser derives
 * file.type from the extension, so a renamed iPhone photo ("IMG_2115.jpg") arrives as image/jpeg while
 * still being HEIC. Null when the file is none of the formats image uploads accept.
 */
export async function sniffImageType(file: Blob): Promise<string | null> {
  const bytes = new Uint8Array(await file.slice(0, 4096).arrayBuffer());
  const ascii = (start: number, length = 4) => String.fromCharCode(...bytes.subarray(start, start + length));
  const startsWith = (...signature: number[]) => signature.every((byte, i) => bytes[i] === byte);

  if (startsWith(0xff, 0xd8, 0xff)) return 'image/jpeg';
  if (startsWith(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png';
  if (ascii(0, 6) === 'GIF87a' || ascii(0, 6) === 'GIF89a') return 'image/gif';
  if (ascii(0) === 'RIFF' && ascii(8) === 'WEBP') return 'image/webp';
  // "BM" alone is too common a start for text; the DIB header that follows has one of a few sizes.
  if (ascii(0, 2) === 'BM' && [12, 40, 52, 56, 64, 108, 124].includes(bytes[14] ?? 0)) return 'image/bmp';
  if (startsWith(0x49, 0x49, 0x2a, 0x00) || startsWith(0x4d, 0x4d, 0x00, 0x2a)) return 'image/tiff';
  if (startsWith(0x00, 0x00, 0x01, 0x00) && (bytes[4] ?? 0) + (bytes[5] ?? 0) > 0) return 'image/x-icon';

  if (ascii(4) === 'ftyp') {
    // Size, "ftyp", major brand, minor version, then the compatible brands up to the size.
    const end = Math.min(new DataView(bytes.buffer).getUint32(0), bytes.length);
    const brands = [ascii(8)];
    for (let offset = 16; offset + 4 <= end; offset += 4) brands.push(ascii(offset));

    if (brands.some((brand) => heicBrands.includes(brand))) return 'image/heic';
    if (brands.some((brand) => avifBrands.includes(brand))) return 'image/avif';
    return null;
  }

  return svgStart.test(new TextDecoder().decode(bytes)) ? 'image/svg+xml' : null;
}

// mif1/msf1 are generic HEIF brands that AVIF lists too, so neither decides anything on its own.
const heicBrands = ['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs'];
const avifBrands = ['avif', 'avis'];

// An <svg> root, after whatever prolog, comments and doctype editors put in front of it.
const svgStart = /^\s*(?:<\?xml[^>]*\?>\s*)?(?:(?:<!--[\s\S]*?-->|<!DOCTYPE[^[>]*(?:\[[\s\S]*?\])?\s*>)\s*)*<svg[\s>]/i;

export function fileNameFromPath(path: string) {
  return path.split('/').pop()!;
}

// Mirrors backend enum
export const PreviewStatus = Object.freeze({
  None: 'None',
  Ready: 'Ready',
  Pending: 'Pending',
  Error: 'Error',
} as const);

export function hasThumbnail(file: { thumbnailUrl?: string | null }) {
  return !!file.thumbnailUrl;
}

export function downloadFileUrl(path: string, name: string) {
  const encodedFilePath = encodeURIComponent(path);
  const encodedFileName = encodeURIComponent(name);
  return `/api/company/download-file?filePath=${encodedFilePath}&downloadFileName=${encodedFileName}`;
}

export function downloadFile(path: string, name: string) {
  window.location.href = downloadFileUrl(path, name);
}

/**
 * Truncates a filename in the middle so the extension is always visible.
 * e.g. "very-long-filename-here.pdf" → "very-lo…re.pdf"
 */
export function truncateFileName(filename: string, maxLength = 16) {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex === -1) {
    if (filename.length <= maxLength) return filename;
    const half = Math.ceil(maxLength / 2) - 1;
    return filename.slice(0, half) + '…' + filename.slice(filename.length - half);
  }
  const name = filename.slice(0, dotIndex);
  const ext = filename.slice(dotIndex);
  const available = maxLength - ext.length;
  if (name.length <= available || available < 4) return filename;
  const half = Math.ceil(available / 2) - 1;
  return name.slice(0, half) + '…' + name.slice(name.length - half) + ext;
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
